<template>
  <v-container>
    <v-card class="d-flex flex-row flex-wrap">
      <v-col cols="10" sm="3" lg="2">
        <h2 class="ml-1 mb-0">Card Types</h2>
      </v-col>
      <v-col cols="1" sm="5">
        <h2>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="createCardTypeModal" v-bind="attrs" v-on="on">
                <v-icon x-large color="blue darken-2">add</v-icon>
              </v-btn>
            </template>
            <span>Create Card Type</span>
          </v-tooltip>
        </h2>
      </v-col>

      <v-col>
        <b-input v-model="search" placeholder="Search" />
      </v-col>
    </v-card>

    <CardTypeTable
      :search="search"
      :items="cardTypeList"
      :loading="isLoadingList"
      @eTablePagination="checkTablePagination"
      @eDeleteCardType="confirmDelete"
      @eViewCardTypeCRUDModal="viewCardTypeCRUDModal"
    />

    <CardTypeCRUDModal
      v-model="cardTypeCRUDModal_Model"
      :dialog="isShowCardTypeCRUDModal"
      :initialData="initialCRUDModalData"
      :entireList="cardTypeList"
      :manualFormReset="isFormReset"
      @eCreateCardType="confirmCreate"
      @eUpdateCardType="confirmUpdate"
      @eCloseCardTypeCRUDModal="closeCardTypeCRUDModal"
    />

    <ConfirmationModal
      ref="confirmation"
      v-model="confirmationModal_Model"
      :isShowDialog="isShowConfirmationModal"
      :inputInfo="confirmationModalInfo"
      :inputColor="confirmationModalInfoColor"
      :inputFunction="confirmationModalFunctions"
      @eConfirm="decideOperation"
      @eCancel="closeConfirmationModal"
    />

    <SnackBarNotifier
      :isShow="isShowSnackBar"
      :message="databaseResponseMessage"
      @eReset="resetSnackBar"
    />
  </v-container>
</template>

<script>
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import CardTypeTable from "@/components/tables/CardTypeTable.vue";
import CardTypeCRUDModal from "@/components/widgets/CardTypeCRUDModal.vue";
import CardTypeCRUDMixin from "@/views/pages/cardtypes/CardTypeCRUDMixin.js";
import ConfirmationModal from "@/components/widgets/ConfirmationModal2.vue";
import SnackBarNotifier from "@/components/widgets/SnackBarNotifier.vue";
import AppSettings from "@/appsettings/appsettings.js";

export default {
  mixins: [CardTypeCRUDMixin],
  components: {
    CardTypeTable,
    CardTypeCRUDModal,
    ConfirmationModal,
    SnackBarNotifier
  },
  data() {
    return {
      search: "",
      isShowCardTypeCRUDModal: false,
      cardTypeCRUDModal_Model: {
        constraints: {}
      },
      initialCRUDModalData: {
        constraints: {}
      },
      modalTitles: {
        newEntry: {
          modalHeading: "Create New Card Type",
          submitButtonTitle: "Save"
        },
        existingEntry: {
          modalHeading: "Card Type Details",
          submitButtonTitle: "Update"
        }
      },
      isFormReset: false,
      isShowConfirmationModal: false,
      confirmationModal_Model: {},
      confirmationModalInfo: {},
      confirmationModalInfoColor: {},
      confirmationModalFunctions: {},
      filter: {
        limit: AppSettings.cardTypePaginationLimit,
        nextToken: null
      },
      cardTypeList: [],
      isLoadingList: false,
      emptyCardType: {
        cardName: null,
        shortCardName: null,
        startingDigits: null,
        constraints: {
          cardLength: null
        }
      },
      isShowSnackBar: false,
      databaseResponseMessage: ""
    };
  },
  methods: {
    resetSnackBar() {
      this.isShowSnackBar = false;
    },
    checkTablePagination(event) {
      if (event.page == event.pageCount && event.pageStop >= event.itemsPerPage)
        this.attemptToLoadMorePages(this.filter);
    },
    async attemptToLoadMorePages(filter) {
      if (this.filter.nextToken) {
        let retrievedData = await this.retrieveAllCardTypes(filter);
        this.cardTypeList = this.cardTypeList.concat(
          retrievedData.cardTypeList
        );
        this.filter.nextToken = retrievedData.nextToken;
      }
    },
    viewCardTypeCRUDModal(cardTypeData) {
      this.initialCRUDModalData = JSON.parse(JSON.stringify(cardTypeData));
      let combinedData = Object.assign({}, cardTypeData);
      this.openCardTypeCRUDModal(combinedData, this.modalTitles.existingEntry);
    },
    createCardTypeModal() {
      this.openCardTypeCRUDModal(this.emptyCardType, this.modalTitles.newEntry);
    },
    openCardTypeCRUDModal(combinedData, modalTitles) {
      this.cardTypeCRUDModal_Model = Object.assign(combinedData, modalTitles);
      this.isShowCardTypeCRUDModal = true;
    },
    closeCardTypeCRUDModal() {
      this.isShowCardTypeCRUDModal = false;
      this.$nextTick(() => {
        this.cardTypeCRUDModal_Model = this.emptyCardType;
      });
    },
    async cardTypeCreate(cardTypeInfo) {
      let retrievedData = await this.createCardType(cardTypeInfo);
      this.displaySnackBar(retrievedData.statusMessage);

      if (retrievedData.statusMessage.includes("Successfully Created")) {
        cardTypeInfo.constraints = JSON.parse(cardTypeInfo.constraints);
        this.cardTypeList.unshift(cardTypeInfo);
        this.isFormReset = !this.isFormReset;
        this.closeCardTypeCRUDModal();
      }
    },
    async cardTypeDelete(shortCardName) {
      let retrievedData = await this.deleteCardType(shortCardName);
      this.displaySnackBar(retrievedData.statusMessage);

      if (retrievedData.statusMessage.includes("Successfully Deleted.")) {
        this.removeCardTypeFromArray(shortCardName);
        this.closeCardTypeCRUDModal();
      }
    },
    async cardTypeUpdate(cardTypeInfo) {
      let retrievedData = await this.updateCardType(cardTypeInfo);
      this.displaySnackBar(retrievedData.statusMessage);

      if (retrievedData.statusMessage.includes("Successfully Updated.")) {
        cardTypeInfo.constraints = JSON.parse(cardTypeInfo.constraints);
        this.updateCardTypeInArray(cardTypeInfo);
        this.isFormReset = !this.isFormReset;
        this.closeCardTypeCRUDModal();
      }
    },
    updateCardTypeInArray(cardTypeInfo) {
      let cardTypeIndex = this.cardTypeList.findIndex(
        list => list.shortCardName === cardTypeInfo.shortCardName
      );
      if (cardTypeIndex >= 0) {
        this.cardTypeList.splice(cardTypeIndex, 1, cardTypeInfo);
      }
    },
    removeCardTypeFromArray(shortCardName) {
      let cardTypeIndex = this.cardTypeList.findIndex(
        list => list.shortCardName === shortCardName
      );
      if (cardTypeIndex >= 0) {
        this.cardTypeList.splice(cardTypeIndex, 1);
      }
    },
    displaySnackBar(message) {
      if (message) {
        this.databaseResponseMessage = message;
        this.isShowSnackBar = true;
      }
    },
    confirmCreate(cardTypeInfo) {
      this.confirmationModalInfo = {
        title: "Create",
        message: `Are You Sure You Want To Create ${cardTypeInfo.shortCardName}?`,
        confirmButtonTitle: "Create"
      };
      this.confirmationModalInfoColor = {
        titleColor: "green",
        confirmButtonTitleColor: "green"
      };
      this.confirmationModal_Model = Object.assign(
        {},
        { cardTypeInfo: cardTypeInfo }
      );
      this.isShowConfirmationModal = true;
    },
    confirmUpdate(cardTypeInfo) {
      this.confirmationModalInfo = {
        title: "Update",
        message: `Are You Sure You Want To Update ${cardTypeInfo.shortCardName}?`,
        confirmButtonTitle: "Update"
      };
      this.confirmationModalInfoColor = {
        titleColor: "yellow",
        confirmButtonTitleColor: "yellow"
      };
      this.confirmationModal_Model = Object.assign(
        {},
        { cardTypeInfo: cardTypeInfo }
      );
      this.isShowConfirmationModal = true;
    },
    confirmDelete(shortCardName) {
      this.confirmationModalInfo = {
        title: "Delete",
        message: `Are You Sure You Want To Delete ${shortCardName}?`,
        confirmButtonTitle: "Delete"
      };
      this.confirmationModalInfoColor = {
        titleColor: "red",
        confirmButtonTitleColor: "red"
      };
      this.confirmationModal_Model = Object.assign(
        {},
        { shortCardName: shortCardName }
      );
      this.isShowConfirmationModal = true;
    },
    async decideOperation(passedData) {
      this.$refs.confirmation.loading();
      switch (passedData.confirmationModalTitle) {
        case "Create":
          await this.cardTypeCreate(passedData.cardTypeInfo);
          return this.closeConfirmationModal();
        case "Update":
          await this.cardTypeUpdate(passedData.cardTypeInfo);
          return this.closeConfirmationModal();
        case "Delete":
          await this.cardTypeDelete(passedData.shortCardName);
          return this.closeConfirmationModal();
        default:
          this.closeConfirmationModal();
          this.displaySnackBar("Check Decider Operation.");
      }
    },
    closeConfirmationModal() {
      this.confirmationModal_Model = {};
      this.$refs.confirmation.stopLoading();
      this.isShowConfirmationModal = false;
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Manage Card Types" }]);
  },
  async created() {
    this.isLoadingList = true;
    let retrievedData = await this.retrieveAllCardTypes(this.filter);
    this.isLoadingList = false;
    this.cardTypeList = retrievedData.cardTypeList;
    this.filter.nextToken = retrievedData.nextToken;
    this.displaySnackBar(retrievedData.statusMessage);
  }
};
</script>
